import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { CheckCircle2 } from 'lucide-react';
import Navbar from '../../components/brand/Navbar';
import Footer from '../../components/brand/Footer';
import FAQSection from '../../components/brand/FAQSection';

const faqs = [
  {
    q: 'Can my support coordinator book on my behalf?',
    a: 'Yes. Support coordinators, plan managers, and family members can all book on behalf of an NDIS participant. When booking, provide the participant\'s name, address, any specific needs or considerations, and the pathology request form details.',
  },
  {
    q: 'How is billing handled for NDIS participants?',
    a: 'We invoice directly to the plan manager or support coordinator after the collection is completed. The participant does not need to pay at the time of the visit. A confirmation email is sent immediately on booking, and a detailed invoice is issued after the collection.',
  },
  {
    q: 'What if the participant has sensory sensitivities or anxiety?',
    a: 'Let us know when you book and we will match the participant with a collector experienced in working with people who have sensory or anxiety-related needs. Our collectors are trained to work at the participant\'s pace, explain each step clearly, and create a calm, low-pressure environment. A carer or support worker can be present during the collection if preferred.',
  },
  {
    q: 'What tests can be done through mobile collection?',
    a: 'Almost any blood test that can be done at a traditional pathology centre can be performed through mobile collection. This includes full blood count, liver and kidney function, cholesterol, thyroid function, iron studies, HbA1c, hormone panels, and most other routine pathology tests. If your doctor has issued a pathology request form, we can almost certainly collect it.',
  },
  {
    q: 'Can you visit group homes or supported independent living arrangements?',
    a: 'Yes. We visit private homes, group homes, supported independent living residences, and any other location where the participant is comfortable. If you have multiple participants in the same location, we can schedule collections back-to-back.',
  },
  {
    q: 'What areas do you service for NDIS participants?',
    a: 'We service NDIS participants across Brisbane, Sydney and Melbourne metros. If your location is outside these areas, contact us and we will let you know if we can accommodate.',
  },
];

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map(({ q, a }) => ({
    '@type': 'Question',
    name: q,
    acceptedAnswer: { '@type': 'Answer', text: a },
  })),
};

const Section = ({ id, title, children }) => (
  <section
    id={id}
    className="py-16"
    style={{ borderBottom: '1px solid rgba(43, 56, 121, 0.08)' }}
  >
    <div className="container max-w-4xl mx-auto px-6">
      <h2
        className="font-['Inter'] font-bold mb-5 text-[#00001E]"
        style={{ fontSize: '1.6rem', letterSpacing: '-0.02em' }}
      >
        {title}
      </h2>
      <div className="text-[#2B3879] leading-relaxed" style={{ fontSize: '1rem' }}>
        {children}
      </div>
    </div>
  </section>
);

const InternalLink = ({ href, children }) => (
  <a href={href} className="text-[#2B3879] hover:underline font-semibold">{children}</a>
);

export default function NDISPathology() {
  useEffect(() => {
    if (window.gtag) {
      window.gtag('event', 'view_service_page', {
        event_category: 'engagement',
        event_label: 'NDIS Pathology'
      });
    }
  }, []);

  return (
    <>
      <Helmet>
        <title>NDIS Pathology Collection | Mobile Blood Tests Brisbane, Sydney &amp; Melbourne | Nura Health</title>
        <meta name="description" content="Mobile pathology collection for NDIS participants across Brisbane, Sydney and Melbourne metros. Support coordinators welcome. Convenient at-home blood testing, invoice billing." />
        <link rel="canonical" href="https://nurahealth.com.au/services/ndis-pathology" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="min-h-screen" style={{ background: '#F7F9FB' }}>
        <Navbar onBookClick={() => (window.location.href = '/booking')} />

        {/* Hero — dark banner, flows from dark navbar */}
        <section
          className="pt-36 pb-20 relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, #00001E 0%, #0a0b2e 60%, #101a3e 100%)',
          }}
        >
          {/* Mint radial glow */}
          <div className="absolute inset-0 pointer-events-none" style={{
            background: 'radial-gradient(ellipse at 20% 60%, rgba(128,229,203,0.09) 0%, transparent 55%)',
          }} />
          <div className="container max-w-4xl mx-auto px-6 relative z-10">
            <span className="pill-label-dark">NDIS</span>
            <h1
              className="font-['Inter'] font-extrabold mb-5"
              style={{ fontSize: 'clamp(2rem, 4vw, 3rem)', lineHeight: 1.1, letterSpacing: '-0.03em', color: '#FFFFFF' }}
            >
              NDIS Pathology Collection — At-Home Blood Tests for Participants
            </h1>
            <p className="text-lg mb-8 max-w-2xl leading-relaxed" style={{ color: 'rgba(255,255,255,0.80)' }}>
              We come to the participant's home or supported accommodation. Support coordinators and plan managers welcome.
              Invoice billing — no upfront payment from participants.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="/booking"
                style={{
                  display: 'inline-block',
                  background: '#80E5CB',
                  color: '#00001E',
                  borderRadius: '8px',
                  padding: '13px 28px',
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  textDecoration: 'none',
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  boxShadow: '0 4px 16px rgba(128,229,203,0.35)',
                }}
              >
                Book NDIS Collection
              </a>
              <a
                href="tel:0420424290"
                style={{
                  display: 'inline-block',
                  background: 'transparent',
                  color: '#FFFFFF',
                  borderRadius: '8px',
                  padding: '12px 26px',
                  fontWeight: 600,
                  fontSize: '0.95rem',
                  textDecoration: 'none',
                  border: '2px solid rgba(255,255,255,0.40)',
                }}
              >
                Call 0420 424 290
              </a>
            </div>
          </div>
        </section>

        <Section title="Pathology Collection Designed for NDIS Participants">
          <p>
            Getting to a pathology collection centre can be a genuine barrier for many NDIS participants. Whether it's
            physical mobility, sensory sensitivities, anxiety, or simply the logistics of coordinating transport and support
            workers, the traditional model doesn't work for everyone. Nura Health removes that barrier entirely — our qualified
            collectors come to the participant's home, group home, or supported accommodation at a time that suits their routine.
          </p>
        </Section>

        <Section title="For Support Coordinators and Plan Managers">
          <p>
            We work directly with support coordinators and plan managers to streamline pathology collection for your
            participants. Bookings can be made on behalf of participants, and we invoice directly — no upfront payment
            required from the participant. Our service is designed to reduce coordination overhead for your team while
            ensuring participants receive timely, professional pathology collection without the stress of attending a clinic.
          </p>
        </Section>

        <Section title="Pricing">
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            {[
              { label: 'NDIS Standard Collection', price: '$60', note: '2+ days notice' },
              { label: 'NDIS Urgent / Same-Day', price: '$80', note: 'Next day or sooner' },
            ].map(({ label, price, note }) => (
              <div
                key={label}
                className="rounded-xl p-5 bg-white"
                style={{ border: '1px solid rgba(43,56,121,0.09)', boxShadow: '0 4px 20px rgba(43,56,121,0.09)' }}
              >
                <p className="text-[#00001E] font-semibold text-lg">{price}</p>
                <p className="text-[#2B3879] font-medium text-sm">{label}</p>
                <p className="text-[#2B3879] text-xs mt-1 opacity-70">{note}</p>
              </div>
            ))}
          </div>
          <p>
            We invoice directly to the plan manager or support coordinator — participants do not need to pay at the
            time of collection. Confirmation emails are sent immediately on booking.
          </p>
        </Section>

        <Section title="How to Book">
          <p>
            Support coordinators, plan managers, and participants can book online through our website or call us directly
            on 0420 424 290. Select 'NDIS' as the service type during booking and we'll handle the rest, including invoice
            billing and direct communication with the participant's care team.
          </p>
          <div className="flex flex-wrap gap-4 mt-6">
            <a
              href="/booking"
              style={{
                display: 'inline-block',
                background: '#80E5CB',
                color: '#00001E',
                borderRadius: '8px',
                padding: '13px 28px',
                fontWeight: 700,
                fontSize: '0.95rem',
                textDecoration: 'none',
              }}
            >
              Book Online
            </a>
            <a
              href="tel:0420424290"
              style={{
                display: 'inline-block',
                background: 'transparent',
                color: '#2B3879',
                borderRadius: '8px',
                padding: '12px 26px',
                fontWeight: 600,
                fontSize: '0.95rem',
                textDecoration: 'none',
                border: '2px solid #2B3879',
              }}
            >
              Call 0420 424 290
            </a>
          </div>
        </Section>

        <Section title="Service Areas">
          <div className="flex flex-wrap gap-2 mt-2">
            {['Brisbane Metro', 'Sydney Metro', 'Melbourne Metro'].map((area) => (
              <span
                key={area}
                className="px-3 py-1 rounded-full text-sm font-medium"
                style={{
                  background: 'rgba(128,229,203,0.14)',
                  color: '#00001E',
                  border: '1px solid rgba(128,229,203,0.45)',
                }}
              >
                {area}
              </span>
            ))}
          </div>
        </Section>

        <FAQSection items={faqs} />

        <section className="py-12 bg-[#F7F9FB]">
          <div className="container max-w-4xl mx-auto px-6">
            <p className="text-[#2B3879] text-sm">
              Also see:{' '}
              <InternalLink href="/services/home-blood-collection">home blood collection</InternalLink>
              {' · '}
              <InternalLink href="/services/aged-care-pathology">aged care facility services</InternalLink>
              {' · '}
              <InternalLink href="/">back to home</InternalLink>
            </p>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
